import React, { useCallback, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import useAndroidDetailBack from '@/hooks/useAndroidDetailBack';
import { agentApiSsr } from '@/lib/agentApi';
import { useAgentStore } from '@/store';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';

function agentCatalogSlugFromParams(params) {
  const raw = params?.slug;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && raw[0]) return String(raw[0]);
  return '';
}

const AgentCatalogDetailNavigationHeaderLeft = React.memo(() => {
  const router = useRouter();
  const { t } = useTranslation();
  useAndroidDetailBack(router, '/agents');

  const onBack = useCallback(() => {
    if (Platform.OS === 'web' && router.canGoBack?.()) {
      router.back();
      return;
    }
    router.replace('/agents');
  }, [router]);

  return (
    <NavigationBarButtonContainer leftPadding>
      <NavigationBarButton
        icon="chevron-left"
        tooltip={t('nav.routes.agentCatalog.backToAgentsTitle')}
        accessibilityLabel={t('nav.routes.agentCatalog.backToAgents')}
        onPress={onBack}
      />
    </NavigationBarButtonContainer>
  );
});

AgentCatalogDetailNavigationHeaderLeft.displayName = 'AgentCatalogDetailNavigationHeaderLeft';

const AgentCatalogDetailNavigationHeaderRight = React.memo(() => {
  const params = useLocalSearchParams();
  const slug = agentCatalogSlugFromParams(params);
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const agentsMap = useAgentStore((s) => s.agents);
  const addAgents = useAgentStore((s) => s.addAgents);
  const startAgentWorkChat = useStartAgentWorkChat();
  const [busy, setBusy] = useState(false);

  const agentFromStore = useMemo(() => {
    if (!slug) return null;
    const key = String(slug).toLowerCase();
    return Object.values(agentsMap || {}).find(
      (a) => a && String(a.slug || '').toLowerCase() === key,
    ) || null;
  }, [agentsMap, slug]);

  const onUseAgent = useCallback(async () => {
    if (!slug || busy) return;
    let agent = agentFromStore;
    if (!agent?.id) {
      setBusy(true);
      try {
        const fetched = await agentApiSsr.getBySlug(slug);
        if (fetched?.id) {
          addAgents([fetched]);
          agent = fetched;
        }
      } catch {
        agent = null;
      } finally {
        setBusy(false);
      }
    }
    if (!agent?.id) return;
    startAgentWorkChat(agent, { source: 'agent_detail_header_use_agent' });
  }, [slug, busy, agentFromStore, addAgents, startAgentWorkChat]);

  const label = t('pages.agents.useThisAgent');

  return (
    <NavigationBarButtonContainer rightPadding>
      <NavigationBarButton
        icon={busy ? 'spinner' : 'comments'}
        solidIcon={busy}
        tooltip={t('pages.agents.useThisAgent')}
        label={isXS ? undefined : label}
        onPress={onUseAgent}
        disabled={!slug || busy}
      />
    </NavigationBarButtonContainer>
  );
});

AgentCatalogDetailNavigationHeaderRight.displayName = 'AgentCatalogDetailNavigationHeaderRight';

/**
 * /agents/[slug] — public catalog agent SEO page; header opens chat like the hero CTA.
 * @param {string} title — header title (agent name or fallback)
 */
export function getAgentCatalogDetailRouteNavigationOptions(title) {
  return {
    title,
    headerTitle: title,
    headerBackVisible: false,
    headerShown: true,
    headerLeft: () => <AgentCatalogDetailNavigationHeaderLeft />,
    headerRight: () => <AgentCatalogDetailNavigationHeaderRight />,
  };
}
